import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { connectPost } from './connectApi';
import './styles/ConnectAuth.css';

/**
 * Landing page for the link in the verification email.
 * Route: /connect/verify-email?token=...
 */
export default function ConnectVerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | done | failed
  const [message, setMessage] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const token = searchParams.get('token');
    if (!token) {
      setStatus('failed');
      setMessage('This link is missing its confirmation code.');
      return;
    }

    const verify = async () => {
      try {
        const result = await connectPost('connect/auth/verify-email', { token });
        if (result.success) {
          setStatus('done');
          setMessage(result.response);
          setTimeout(() => navigate('/connect'), 4000);
        } else {
          setStatus('failed');
          setMessage(result.response || 'This link could not be confirmed.');
          setIsExpired(!!result.data?.expired);
        }
      } catch (err) {
        setStatus('failed');
        setMessage('Could not reach the server. Please try again.');
      }
    };

    verify();
  }, [searchParams, navigate]);

  const handleResend = async () => {
    if (!resendEmail.trim() || resendState === 'sending') return;
    setResendState('sending');
    try {
      await connectPost('connect/auth/resend-verification', { email: resendEmail.trim() });
      setResendState('sent');
    } catch (err) {
      setResendState('idle');
    }
  };

  return (
    <div className="connect-auth-page">
      <div className="connect-auth-bg">
        <div className="connect-auth-bg-gradient"></div>
        <div className="connect-auth-bg-pattern"></div>
      </div>

      <div className="connect-auth-container">
        <div className="connect-auth-branding">
          <Link to="/" className="connect-auth-logo-link">
            <img
              src={process.env.PUBLIC_URL + '/assets/logo.png'}
              alt="OutCaribbean"
              className="connect-auth-logo"
            />
          </Link>
          <h1 className="connect-auth-title">Community App</h1>
        </div>

        <div className="connect-auth-card">
          <div className="connect-verify-panel">
            {status === 'checking' && (
              <>
                <div className="connect-verify-icon">✉️</div>
                <h2>Confirming your email...</h2>
                <p>One moment.</p>
              </>
            )}

            {status === 'done' && (
              <>
                <div className="connect-verify-icon">🌈</div>
                <h2>Email confirmed</h2>
                <p>{message}</p>
                <Link to="/connect" className="connect-submit-btn connect-verify-btn">
                  Go to sign in
                </Link>
              </>
            )}

            {status === 'failed' && (
              <>
                <div className="connect-verify-icon">⚠️</div>
                <h2>Link didn't work</h2>
                <p>{message}</p>

                {isExpired && resendState !== 'sent' && (
                  <div className="connect-verify-resend">
                    <label className="connect-field-label">Send a new link to</label>
                    <input
                      type="email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      className="connect-field-input"
                      placeholder="your@email.com"
                    />
                    <button
                      className="connect-submit-btn connect-verify-btn"
                      onClick={handleResend}
                      disabled={resendState === 'sending' || !resendEmail.trim()}
                    >
                      {resendState === 'sending' ? 'Sending...' : 'Send new link'}
                    </button>
                  </div>
                )}

                {resendState === 'sent' && (
                  <p className="connect-verify-sent">
                    If that address needs confirming, a new link is on its way.
                  </p>
                )}

                <Link to="/connect" className="connect-back-link connect-verify-back">
                  Back to sign in
                </Link>
              </>
            )}
          </div>
        </div>

        <div className="connect-auth-footer">
          <Link to="/" className="connect-back-link">
            ← Back to OutCaribbean
          </Link>
        </div>
      </div>
    </div>
  );
}
